import React, { useState, useRef, useEffect, useCallback } from "react";
import { useStickyNav } from "../../../hooks/UseStickyNav";
import WhiteLogo from "../../../assets/images/logo-itm-white.png";
import ContactButton from "../ContactComponent/ContactButton";
import ContactComponent from "../ContactComponent/ContactComponent";
import LanguageSelectorNavbar from "../../LanguageSelector/LanguageSelectorNavbar";
import i18n from "../../../i18n/i18n";
import "./Navbar.css";
import Toplogo from "../../../assets/images/logo-itm-white.png";
import Bluelogo from "../../../assets/images/logo-itm-blue.png";
import { useTranslation } from "react-i18next";
import { LiquidButton } from "../../ui/liquid-glass-button";

interface NavbarProps {
  sectionRefs: {
    header: React.RefObject<HTMLDivElement>;
    start: React.RefObject<HTMLDivElement>;
    services: React.RefObject<HTMLDivElement>;
    start2: React.RefObject<HTMLDivElement>;
    cloud: React.RefObject<HTMLDivElement>;
    expertise: React.RefObject<HTMLDivElement>;
    ai: React.RefObject<HTMLDivElement>;
    contact: React.RefObject<HTMLDivElement>;
    blog: React.RefObject<HTMLDivElement>;
    newsletter: React.RefObject<HTMLDivElement>;
  };
  openContactModal: () => void;
  closeContactModal: () => void;
}

const HamburgerIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="32"
    height="32"
    viewBox="0 0 24 24"
    className="tw-inline tw-align-baseline"
  >
    <path 
      fill="currentColor" 
      stroke="" 
      strokeWidth="0.3" 
      d="M19 9.998H5v-2h14zm0 6H5v-2h14z" 
    />
  </svg>
);

const CloseIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="32"
    height="32"
    viewBox="0 0 24 24"
    className="tw-inline tw-align-baseline"
  >
    <path
      fill="currentColor"
      d="m12 13.4l-2.9 2.9q-.275.275-.7.275t-.7-.275t-.275-.7t.275-.7l2.9-2.9l-2.9-2.875q-.275-.275-.275-.7t.275-.7t.7-.275t.7.275l2.9 2.9l2.875-2.9q.275-.275.7-.275t.7.275q.3.3.3.713t-.3.687L13.375 12l2.9 2.9q.275.275.275.7t-.275.7q-.3.3-.712.3t-.688-.3z"
    />
  </svg>
);

const Navbar: React.FC<NavbarProps> = ({ sectionRefs }) => {
  const { isSticky, showNav, setShowNav } = useStickyNav({
    scrollThreshold: 10,
    stickyOffset: 10,
  });

  const [activeDropdown, setActiveDropdown] = useState<
    null | "academy" | "cloud" | "cyber" | "ai" | "home" | "news" | "aiLab"
  >(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDarkBackground, setIsDarkBackground] = useState(false);
  const [isAtTop, setIsAtTop] = useState(true);
  const [navbarHeight, setNavbarHeight] = useState(0);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 0
  );
  const [dropdownOffset, setDropdownOffset] = useState(0);
  const dropdownOffsetRef = useRef(0);

  const [hoveredLink, setHoveredLink] = useState<string | null>(null);

  const navbarRef = useRef<HTMLDivElement>(null);
  const cloudMenuRef = useRef<HTMLDivElement>(null);
  const cyberMenuRef = useRef<HTMLDivElement>(null);
  const academyMenuRef = useRef<HTMLDivElement>(null);
  const homeMenuRef = useRef<HTMLDivElement>(null);

  const cloudDropdownRef = useRef<HTMLDivElement>(null);
  const cyberDropdownRef = useRef<HTMLDivElement>(null);
  const academyDropdownRef = useRef<HTMLDivElement>(null);

  const updateDropdownAlignment = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (navbarRef.current && homeMenuRef.current) {
      const navbarInner = navbarRef.current.querySelector('.navbar-inner') as HTMLElement | null;
      const referenceContainer = navbarInner ?? navbarRef.current;
      const containerRect = referenceContainer.getBoundingClientRect();
      const homeRect = homeMenuRef.current.getBoundingClientRect();
      const offset = Math.max(0, homeRect.left - containerRect.left);
      if (dropdownOffsetRef.current !== offset) {
        dropdownOffsetRef.current = offset;
        setDropdownOffset(offset);
      }
    }
  }, []);

  const [hoveredSubLink, setHoveredSubLink] = useState<string | null>(null);

  const { t } = useTranslation();
  const currentLanguage = i18n.language || 'fr';

  useEffect(() => {
    setHoveredLink(null);
  }, [currentLanguage]);
  
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
      updateDropdownAlignment();
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [updateDropdownAlignment]);

  useEffect(() => {
    updateDropdownAlignment();
  }, [viewportWidth, isSticky, isAtTop, updateDropdownAlignment]);

  const getLangPath = (path: string) => {
    if (currentLanguage === 'en') {
      if (path.startsWith('/fr/') || path.startsWith('/en/')) {
        return `/en${path.substring(3)}`;
      } 
      else if (path === '/fr' || path === '/en') {
        return `/en`;
      }
      else if (path.startsWith('/')) {
        return `/en${path}`;
      }
      return `/en/${path}`;
    } 
    else {
      if (path.startsWith('/fr/') || path.startsWith('/en/')) {
        return path.substring(3);
      }
      else if (path === '/fr' || path === '/en') {
        return `/`;
      }
      return path;
    }
  };

  const handleMenuMouseEnter = (dropdown: typeof activeDropdown) => {
    setActiveDropdown(dropdown);
    updateDropdownAlignment();
  };

  const handleMenuMouseLeave = () => {
    setActiveDropdown(null);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => !prev);
    if (isSmallScreen) {
      setShowNav(true);
    }
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setCloudDropdownOpen(false);
    setCyberDropdownOpen(false);
    setAcademyDropdownOpen(false);
  };

  const handleMenuMouseLeaveDropdown = useCallback(
    (e: React.MouseEvent) => {
      if (activeDropdown === "cloud" || activeDropdown === "cyber" || activeDropdown === "academy") {
        if (e.clientY > window.innerHeight / 2) {
          setActiveDropdown(null);
        }
      } else {
        setActiveDropdown(null);
      }
    },
    [activeDropdown]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (activeDropdown === "cloud" || activeDropdown === "cyber" || activeDropdown === "academy") {
        const target = event.target as Node;

        const isClickInsideCloudMenu = cloudMenuRef.current?.contains(target);
        const isClickInsideCyberMenu = cyberMenuRef.current?.contains(target);
        const isClickInsideAcademyMenu = academyMenuRef.current?.contains(target);
        const isClickInsideCloudDropdown =
          cloudDropdownRef.current?.contains(target);
        const isClickInsideCyberDropdown =
          cyberDropdownRef.current?.contains(target);
        const isClickInsideAcademyDropdown =
          academyDropdownRef.current?.contains(target);

        if (
          !isClickInsideCloudMenu &&
          !isClickInsideCyberMenu &&
          !isClickInsideAcademyMenu &&
          !isClickInsideCloudDropdown &&
          !isClickInsideCyberDropdown &&
          !isClickInsideAcademyDropdown
        ) {
          setActiveDropdown(null);
        }
      }
    };

    if (activeDropdown === "cloud" || activeDropdown === "cyber" || activeDropdown === "academy") {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [activeDropdown]);

  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target as Node)
      ) {
        closeMobileMenu();
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "hidden";
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "auto";
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "auto";
    };
  }, [isMobileMenuOpen]);

  const [isMobileMenuVisible, setIsMobileMenuVisible] = useState(false);

  useEffect(() => {
    if (isMobileMenuOpen) {
      setIsMobileMenuVisible(true);
    } else if (isMobileMenuVisible) {
      const timer = setTimeout(() => {
        setIsMobileMenuVisible(false);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [isMobileMenuOpen, isMobileMenuVisible]);

  useEffect(() => {
    const handleScroll = () => {
      setIsAtTop(window.scrollY === 0);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    setNavbarHeight(navbarRef.current?.offsetHeight || 0);
  }, [navbarRef, isSticky]);

  useEffect(() => {
    if (!sectionRefs) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.05) {
            const matchedKey = (Object.entries(sectionRefs).find(
              ([, ref]) => ref.current === entry.target
            ) || [])[0];

            console.log(
              `Section: ${matchedKey}, 
               Ratio: ${entry.intersectionRatio.toFixed(2)}, 
               IsIntersecting: ${entry.isIntersecting}`
            );

            if (
              matchedKey === "header" ||
              matchedKey === "expertise" ||
              matchedKey === "newsletter" ||
              matchedKey === "contact" ||
              entry.target.closest('.gradient-bg') ||
              entry.target.querySelector('.gradient-bg')
            ) {
              setIsDarkBackground(true);
            } else {
              setIsDarkBackground(false);
            }
          }
        });
      },
      {
        root: null,
        rootMargin: `-${navbarHeight}px 0px -85% 0px`,
        threshold: [0, 0.05, 0.1, 0.2],
      }
    );

    setTimeout(() => {
      for (const key in sectionRefs) {
        const sentinelEl = sectionRefs[key as keyof typeof sectionRefs].current;
        if (sentinelEl) {
          observer.observe(sentinelEl);
        }
      }
    }, 100);

    return () => {
      observer.disconnect();
    };
  }, [sectionRefs, navbarHeight]);

  const getSubLinkClass = (subLinkName: string) => {
    if (hoveredSubLink === subLinkName) {
      return "active-sub-link";
    } else if (hoveredSubLink !== null) {
      return "inactive-sub-link";
    }
    return "";
  };

  const handleMouseMoveCloseDropdown = useCallback(
    (e: MouseEvent) => {
      if (
        (activeDropdown === "cloud" || activeDropdown === "cyber" || activeDropdown === "academy") &&
        e.clientY > window.innerHeight / 2
      ) {
        setActiveDropdown(null);
      }
    },
    [activeDropdown]
  );

  useEffect(() => {
    if (activeDropdown === "cloud" || activeDropdown === "cyber" || activeDropdown === "academy") {
      document.addEventListener("mousemove", handleMouseMoveCloseDropdown);
      document.body.style.overflow = "hidden";
    } else {
      document.removeEventListener("mousemove", handleMouseMoveCloseDropdown);
      document.body.style.overflow = "auto";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMoveCloseDropdown);
      document.body.style.overflow = "auto";
    };
  }, [activeDropdown, handleMouseMoveCloseDropdown]);

  useEffect(() => {
    if (isContactModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isContactModalOpen]);

  const handleOpenContactModal = () => {
    setIsContactModalOpen(true);
    closeMobileMenu();
  };

  const handleCloseContactModal = () => {
    setIsContactModalOpen(false);
  };

  const [cloudDropdownOpen, setCloudDropdownOpen] = useState(false);
  const [cyberDropdownOpen, setCyberDropdownOpen] = useState(false);
  const [academyDropdownOpen, setAcademyDropdownOpen] = useState(false);

  const toggleCloudDropdown = () => {
    setCloudDropdownOpen(!cloudDropdownOpen);
    setCyberDropdownOpen(false);
    setAcademyDropdownOpen(false);
  };

  const toggleCyberDropdown = () => {
    setCyberDropdownOpen(!cyberDropdownOpen);
    setCloudDropdownOpen(false);
    setAcademyDropdownOpen(false);
  };

  const toggleAcademyDropdown = () => {
    setAcademyDropdownOpen(!academyDropdownOpen);
    setCloudDropdownOpen(false);
    setCyberDropdownOpen(false);
  };

  const getLinkClass = (dropdownName: string | null, linkName: string) => {
    if (activeDropdown === dropdownName) {
      return "active-link";
    } else if (activeDropdown !== null) {
      return "inactive-link";
    }
    if (hoveredLink === linkName) {
      return "active-link";
    } else if (hoveredLink !== null) {
      return "inactive-link";
    }
    return "";
  };

  const isDropdownOpen = cloudDropdownOpen || cyberDropdownOpen || academyDropdownOpen;
  const activeDropdownName = cloudDropdownOpen
    ? "cloud"
    : cyberDropdownOpen
    ? "cyber"
    : academyDropdownOpen
    ? "academy"
    : null;

  const getDimmedClass = (item: string) => {
    if (!isDropdownOpen) return "";
    if (item === "cloud" && activeDropdownName === "cloud") return "";
    if (item === "cyber" && activeDropdownName === "cyber") return "";
    if (item === "academy" && activeDropdownName === "academy") return "";
    return "dimmed";
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const navbar = document.querySelector('.navbar');
      if (scrollY > 100) {
        navbar?.classList.add('scrolled');
      } else {
        navbar?.classList.remove('scrolled');
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth <= 480);
      setViewportWidth(window.innerWidth);
      updateDropdownAlignment();
    };
    
    checkScreenSize();
    
    window.addEventListener('resize', checkScreenSize);
    
    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  const getNavbarClasses = () => {
    const visibilityClass = "visible";
    
    let themeClass;
    if (isSmallScreen) {
      themeClass = isAtTop || isMobileMenuOpen ? "top-theme-home" : "dark-theme";
    } else {
      themeClass = isAtTop
        ? "top-theme-home"
        : isDarkBackground && activeDropdown
        ? "top-theme-home"
        : isDarkBackground
        ? "dark-theme"
        : "light-theme";
    }
    
    return `header-nav ${
      isSticky ? "sticky" : ""
    } ${visibilityClass} ${themeClass} ${
      isDropdownOpen ? "mobile-dropdown-open" : ""
    } ${activeDropdown ? "dropdown-open" : ""} ${
      hoveredLink ? "hover-open" : ""
    }`;
  };

  const getMobileMenuClasses = () => {
    const themeClass = isSmallScreen 
      ? (isAtTop ? "top-theme-home" : "dark-theme") 
      : (isDarkBackground ? "dark-theme" : "light-theme");
    
    return `mobile-nav-menu ${isMobileMenuOpen ? "open" : "close"} ${themeClass}`;
  };

  const getLogoForTheme = () => {
    if (isAtTop || (isDarkBackground && activeDropdown)) {
      return Toplogo;
    } else if (isDarkBackground) {
      return WhiteLogo;
    } else {
      return Bluelogo;
    }
  };


  const getLanguageSelectorTheme = () => {
    if (isSmallScreen) {
      return isAtTop || isMobileMenuOpen;
    } else {
      return isAtTop || (isDarkBackground && !!activeDropdown) || isDarkBackground;
    }
  };

  return (
    <>
      <nav
        ref={navbarRef}
        className={getNavbarClasses()}
      >
        <div className="header-wrapper navbar-inner">
        <div className="itmonkeys-logo">
          <a href={getLangPath('/')}>
            <img src={getLogoForTheme()} alt="Logo IT Monkeys" className="itmonkeys-logo-w" />
          </a>
        </div>

        <div
          className={`header-nav-menu desktop-menu ${
            activeDropdown ? "dropdown-open" : ""
          }`}
        >
          <div
            className={`academy-item ${
              activeDropdown === "home" ? "open" : ""
            }`}
            ref={homeMenuRef}
            onMouseEnter={() => {
              handleMenuMouseEnter("home");
              setHoveredLink("Home");
            }}
            onMouseLeave={() => {
              handleMenuMouseLeave();
              setHoveredLink(null);
            }}
          >
            <a
              href={getLangPath('/')}
              className={`navbar-link ${getLinkClass(
                "home",
                "NextGen Cloud"
              )}`}
            >
              {t('navbar.home', 'Accueil')}
            </a>
          </div>

          <div
            className={`academy-item ${
              activeDropdown === "cloud" ? "open" : ""
            }`}
            onMouseEnter={() => {
              handleMenuMouseEnter("cloud");
              setHoveredLink("NextGen Cloud");
            }}
            onMouseLeave={(e) => {
              handleMenuMouseLeaveDropdown(e);
              setHoveredLink(null);
            }}
          >
            <a
              href="#"
              className={`navbar-link ${getLinkClass(
                "cloud",
                "NextGen Cloud"
              )}`}
            >
              {t('navbar.cloud.title', 'NextGen Cloud')}
            </a>
          </div>

          <div
            className={`academy-item ${
              activeDropdown === "cyber" ? "open" : ""
            }`}
            onMouseEnter={() => {
              handleMenuMouseEnter("cyber");
              setHoveredLink("NextGen Cybersecurity");
            }}
            onMouseLeave={(e) => {
              handleMenuMouseLeaveDropdown(e);
              setHoveredLink(null);
            }}
          >
            <a
              href="#"
              className={`navbar-link ${getLinkClass(
                "cyber",
                "NextGen Cybersecurity"
              )}`}
            >
              {t('navbar.cyber.title', 'NextGen Cybersecurity')}
            </a>
          </div>

          <div
            ref={academyMenuRef}
            className={`academy-item ${
              activeDropdown === "academy" ? "open" : ""
            }`}
            onMouseEnter={() => {
              handleMenuMouseEnter("academy");
              setHoveredLink("ITM Academy");
            }}
            onMouseLeave={(e) => {
              handleMenuMouseLeaveDropdown(e);
              setHoveredLink(null);
            }}
          >
            <a
              href="#"
              className={`navbar-link ${getLinkClass(
                "academy",
                "ITM Academy"
              )}`}
            >
              {t('navbar.academy.title', 'ITM Academy')}
            </a>
          </div>
          {/*        
          <div
            className={`academy-item ${
              activeDropdown === "aiLab" ? "open" : ""
            }`}
            onMouseEnter={() => {
              handleMenuMouseEnter("aiLab");
              setHoveredLink("AI Lab");
            }}
            onMouseLeave={() => {
              handleMenuMouseLeave();
              setHoveredLink(null);
            }}
          >
            <a
              href={getLangPath('/ai/lab')}
              className={`academy-link ${getLinkClass(
                "aiLab",
                "AI Lab"
              )}`}
            >
              {t('navbar.aiLab.title', 'Lab IA')}
            </a>
           
          </div>
          */}

          <div
            className={`academy-item ${
              activeDropdown === "news" ? "open" : ""
            }`}
            onMouseEnter={() => {
              handleMenuMouseEnter("news");
              setHoveredLink("news");
            }}
            onMouseLeave={() => {
              handleMenuMouseLeave();
              setHoveredLink(null);
            }}
          >
            {/*   <a
              href={getLangPath('/cyberguerre')}
              className={`academy-link ${getLinkClass(
                "news",
                "Cyber News"
              )}`}
            >
              {t('navbar.cyberwar', 'Cyberguerre')}
            </a>*/}
          </div>
          <div
            className="contact-button-container"
            data-hover-state={hoveredLink === "Contact" ? "active" : hoveredLink ? "inactive" : "normal"}
            onMouseEnter={() => {
              setHoveredLink("Contact");
              setActiveDropdown(null);
            }}
            onMouseLeave={() => setHoveredLink(null)}
          >
            <ContactButton
              handleOpenContactModal={handleOpenContactModal}
              className=""
            />
          </div>
        </div>

        {/* Right side elements - Language Selector and Login */}
        <div className="navbar-right-elements">
          {/* Language Selector */}
          <div
            className={`language-selector-container`}
            data-hover-state={hoveredLink === "LanguageSelector" ? "active" : hoveredLink ? "inactive" : "normal"}
            style={{
              marginRight: '15px',
              top: '-1px',
              position: 'relative',
              overflow: 'visible'
            }}
            onMouseEnter={() => {
              setHoveredLink("LanguageSelector");
              setActiveDropdown(null);
            }}
            onMouseLeave={() => setHoveredLink(null)}
          >
            <LanguageSelectorNavbar
              isDarkTheme={getLanguageSelectorTheme()}
            />
          </div>

          <div
            className="connexion-button-container"
            data-hover-state={hoveredLink === "Connexion" ? "active" : hoveredLink ? "inactive" : "normal"}
            style={{
              display: 'flex',
              alignItems: 'center'
            }}
            onMouseEnter={() => {
              setHoveredLink("Connexion");
              setActiveDropdown(null);
            }}
            onMouseLeave={() => setHoveredLink(null)}
          >
            <a href="https://app.itmonkeys.fr/" target="_blank" rel="noopener noreferrer">
              <LiquidButton
                size="sm"
                className="connexion-liquid-button"
                style={{
                  fontSize: '14px',
                  fontWeight: '400',
                  top:'1px'
                }}
              >
                {t('common.connexion', 'Connexion')}
              </LiquidButton>
            </a>
          </div>
        </div>

        </div>
        <div className="mobile-menu-icon" onClick={toggleMobileMenu}>
          {isMobileMenuOpen ? <CloseIcon /> : <HamburgerIcon />}
        </div>
      </nav>

      {isContactModalOpen && (
        <ContactComponent isOpen={true} onClose={handleCloseContactModal} />
      )}

      {isMobileMenuVisible && (
        <div
          className={getMobileMenuClasses()}
          ref={mobileMenuRef}
        >
          <a
            href={getLangPath('/')}
            onClick={closeMobileMenu}
            className={`mobile-nav-link mobile-nav-link--mb12 ${getDimmedClass("accueil")}`}
          >
            {t('navbar.home', 'Accueil')}
          </a>
          <a
            href="#"
            onClick={toggleCloudDropdown}
            className={`mobile-nav-link ${getDimmedClass(
              "cloud"
            )} ${cloudDropdownOpen ? "underlined" : ""}`}
          >
            {t('navbar.cloud.title', 'NextGen Cloud')}
          </a>
          <div className={`mobile-academy-dropdown ${cloudDropdownOpen ? "open-padding" : ""} ${cloudDropdownOpen ? "open" : ""}`}>
            <a href={getLangPath('/cloudmigration')} onClick={closeMobileMenu}>
              {t('navbar.cloud.migration', 'Migration vers le cloud')}
            </a>
            <a href={getLangPath('/cloudadmin')} onClick={closeMobileMenu}>
              {t('navbar.cloud.admin', 'Administration cloud')}
            </a>
            <a href={getLangPath('/secureteleworking')} onClick={closeMobileMenu}>
              {t('navbar.cloud.infrastructure', 'Sécuriser le télétravail')}
            </a>
            <a href={getLangPath('/privatecloud')} onClick={closeMobileMenu}>
              {t('navbar.cloud.privateCloud', 'Cloud privé sur NAS')}
            </a>
          </div>

          <a
            href="#"
            onClick={toggleCyberDropdown}
            className={`mobile-nav-link ${getDimmedClass(
              "cyber"
            )} ${cyberDropdownOpen ? "underlined" : ""}`}
          >
            {t('navbar.cyber.title', 'NextGen Cybersecurity')}
          </a>
          <div className={`mobile-academy-dropdown ${cyberDropdownOpen ? "open-padding" : ""} ${cyberDropdownOpen ? "open" : ""}`}>
            <a href={getLangPath('/cyberaudit')} onClick={closeMobileMenu}>
              {t('navbar.cyber.audit', 'Audit de sécurité')}
            </a>
            <a href={getLangPath('/cyberincident')} onClick={closeMobileMenu}>
              {t('navbar.cyber.incident', 'Réponse aux incidents')}
            </a>
            <a href={getLangPath('/sentinel')} onClick={closeMobileMenu}>
              {t('navbar.cyber.sentinel', 'Data Leaks Sentinel')}
            </a>
            <a href={getLangPath('/devsecops')} onClick={closeMobileMenu}>
              {t('navbar.cyber.devSecOps', 'DevSecOps')}
            </a>
          </div>
          <a
            href="#"
            onClick={toggleAcademyDropdown}
            className={`mobile-nav-link ${getDimmedClass(
              "academy"
            )} ${academyDropdownOpen ? "underlined" : ""}`}
          >
            {t('navbar.academy.title', 'ITM Academy')}
          </a>
          <div className={`mobile-academy-dropdown ${academyDropdownOpen ? "open-padding" : ""} ${academyDropdownOpen ? "open" : ""}`}>
          <a
              href={getLangPath('/academy/cloudfundamentals')}
              onClick={closeMobileMenu}
              className={`dropdown-sub-link ${getSubLinkClass("Formation Azure")}`}
              onMouseEnter={() => setHoveredSubLink("Formation Azure")}
              onMouseLeave={() => setHoveredSubLink(null)}
            >
              {t('navbar.academy.cloudFundamentals', 'Azure Cloud Fundamentals')}
            </a>
            <a
              href={getLangPath('/academy/securitycloud')}
              className={`dropdown-sub-link ${getSubLinkClass("Formation Entra")}`}
              onMouseEnter={() => setHoveredSubLink("Formation Entra")}
              onMouseLeave={() => setHoveredSubLink(null)}
            >
              {t('navbar.academy.securityCloud', 'Formation Sécurité & Architecture Cloud')}
            </a>
            <a
              href={getLangPath('/academy/azureadmin')}
              className={`dropdown-sub-link ${getSubLinkClass("Administration Azure")}`}
              onMouseEnter={() => setHoveredSubLink("Administration Azure")}
              onMouseLeave={() => setHoveredSubLink(null)}
            >
              {t('navbar.academy.azureAdmin', 'Azure admin')}
            </a>
          </div>

          {/*
          <a
            href={getLangPath('/cyberguerre')}
            onClick={closeMobileMenu}
            className={`mobile-nav-link ${getDimmedClass("news")}`}
          >
            {t('navbar.cyberwar', 'Cyberguerre')}
          </a>
          */}

          {/*
          <a
            href={getLangPath('/ai/lab')}
            onClick={closeMobileMenu}
            className={`mobile-nav-link ${getDimmedClass("aiLab")}`}
          >
            {t('navbar.aiLab.title', 'Lab IA')}
          </a>
          */}
          <div
            onMouseEnter={() => setHoveredLink("Contact")}
            onMouseLeave={() => setHoveredLink(null)}
          >
            <div className={`mobile-contact-link ${getDimmedClass("contact")}`}>
              <ContactButton
                handleOpenContactModal={handleOpenContactModal}
                className="mobile-contact-link"
              />
            </div>
          </div>
          
          {/* Mobile Language Selector */}
          <div className="mobile-language-selector">
            <div className="mobile-section-links">
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  i18n.changeLanguage('fr');
                  closeMobileMenu();
                  
                  const path = window.location.pathname;
                  if (path.startsWith('/en/')) {
                    window.location.href = path.substring(3);
                  } else if (path === '/en') {
                    window.location.href = '/';
                  }
                }}
                className={i18n.language === 'fr' ? 'active' : ''}
              >
                {t('navbar.languages.french', 'Français')}
              </a>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  i18n.changeLanguage('en');
                  closeMobileMenu();
                  
                  const path = window.location.pathname;
                  if (path.startsWith('/en')) {
                  } else if (path === '/') {
                    window.location.href = '/en';
                  } else {
                    window.location.href = `/en${path}`;
                  }
                }}
                className={i18n.language === 'en' ? 'active' : ''}
              >
                {t('navbar.languages.english', 'English')}
              </a>
            </div>
          </div>
        </div>
      )}

      <div
        className={`academy-dropdown ${
          isAtTop ? "desktop-academy-dropdown-top" : "desktop-academy-dropdown"
        } ${
          activeDropdown === "academy" ? "open" : ""
        }`}
        style={{
          top: (isSticky && navbarRef.current) || isAtTop ? navbarRef.current?.offsetHeight || 60 : "auto",
        }}
      >
        <div style={{
          maxWidth: '1648px',
          margin: '0 auto',
          width: '100%',
          paddingLeft: '20px',
          paddingRight: '20px'
        }}>
          <div
            className="academy-dropdown-content"
            style={{
              marginLeft: `max(${dropdownOffset}px, 0px)`,
              marginRight: 'auto'
            }}
          >
            <div className="column" style={{ width: '100%' }}>
              <span className="section-title">{t('navbar.academy.section', 'Parcours administrateur cloud et sécurité')}</span>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div className="column left-column">
                  <a
                    href={getLangPath('/academy/cloudfundamentals')}
                    className={`dropdown-sub-link ${getSubLinkClass("Formation Azure")}`}
                    onMouseEnter={() => setHoveredSubLink("Formation Azure")}
                    onMouseLeave={() => setHoveredSubLink(null)}
                  >
                    {t('navbar.academy.cloudFundamentals', 'Azure Cloud Fundamentals')}
                  </a>
                  <a
                    href={getLangPath('/academy/securitycloud')}
                    className={`dropdown-sub-link ${getSubLinkClass("Formation Entra")}`}
                    onMouseEnter={() => setHoveredSubLink("Formation Entra")}
                    onMouseLeave={() => setHoveredSubLink(null)}
                  >
                    {t('navbar.academy.securityCloud', 'Formation Sécurité & Architecture Cloud')}
                  </a>
                </div>
                <div className="column right-column">
                  <a
                    href={getLangPath('/academy/azureadmin')}
                    className={`dropdown-sub-link ${getSubLinkClass("Administration Azure")}`}
                    onMouseEnter={() => setHoveredSubLink("Administration Azure")}
                    onMouseLeave={() => setHoveredSubLink(null)}
                  >
                    {t('navbar.academy.azureAdmin', 'Administration Cloud Azure')}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        ref={cloudDropdownRef}
        className={`academy-dropdown ${
          isAtTop ? "desktop-academy-dropdown-top" : "desktop-academy-dropdown"
        } ${
          activeDropdown === "cloud" ? "open" : ""
        }`}
        style={{
          top: (isSticky && navbarRef.current) || isAtTop ? navbarRef.current?.offsetHeight || 60 : "auto",
        }}
      >
        <div style={{
          maxWidth: '1648px',
          margin: '0 auto',
          width: '100%',
          paddingLeft: '20px',
          paddingRight: '20px'
        }}>
          <div
            className="academy-dropdown-content"
            style={{ marginLeft: `${dropdownOffset}px`, marginRight: 'auto' }}
          >
            <div className="column" style={{ width: '100%' }}>
              <span className="section-title">{t('navbar.cloud.section', 'Services cloud')}</span>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div className="column left-column">
                  <a
                    href={getLangPath('/cloudmigration')}
                    className={`dropdown-sub-link ${getSubLinkClass(
                      "Migration vers Azure"
                    )}`}
                    onMouseEnter={() => setHoveredSubLink("Migration vers le Cloud")}
                    onMouseLeave={() => setHoveredSubLink(null)}
                  >
                    {t('navbar.cloud.migration', 'Migration vers le cloud')}
                  </a>
                  <a
                    href={getLangPath('/secureteleworking')}
                    className={`dropdown-sub-link ${getSubLinkClass(
                      "Sécuriser le télétravail"
                    )}`}
                    onMouseEnter={() =>
                      setHoveredSubLink("Sécuriser le télétravail")
                    }
                    onMouseLeave={() => setHoveredSubLink(null)}
                  >
                    {t('navbar.cloud.infrastructure', 'Sécuriser le télétravail')}
                  </a>
                </div>

                <div className="column right-column">
                  <a
                    href={getLangPath('/cloudadmin')}
                    className={`dropdown-sub-link ${getSubLinkClass(
                      "Services Managés Azure"
                    )}`}
                    onMouseEnter={() =>
                      setHoveredSubLink("Administration cloud")
                    }
                    onMouseLeave={() => setHoveredSubLink(null)}
                  >
                    {t('navbar.cloud.admin', 'Administration cloud')}
                  </a>
                  <a
                    href={getLangPath('/privatecloud')}
                    className={`dropdown-sub-link ${getSubLinkClass("Cloud Privé")}`}
                    onMouseEnter={() => setHoveredSubLink("Cloud Privé")}
                    onMouseLeave={() => setHoveredSubLink(null)}
                  >
                    {t('navbar.cloud.privateCloud', 'Cloud privé sur NAS')}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        ref={cyberDropdownRef}
        className={`academy-dropdown ${
          isAtTop ? "desktop-academy-dropdown-top" : "desktop-academy-dropdown"
        } ${
          activeDropdown === "cyber" ? "open" : ""
        }`}
        style={{
          top: (isSticky && navbarRef.current) || isAtTop ? navbarRef.current?.offsetHeight || 60 : "auto",
        }}
      >
        <div style={{
          maxWidth: '1648px',
          margin: '0 auto',
          width: '100%',
          paddingLeft: '20px',
          paddingRight: '20px'
        }}>
          <div
            className="academy-dropdown-content"
            style={{ marginLeft: `${dropdownOffset}px`, marginRight: 'auto' }}
          >
            <div className="column" style={{ width: '100%' }}>
              <span className="section-title">{t('navbar.cyber.section', 'Services en cybersécurité')}</span>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div className="column left-column">
                  <a
                    href={getLangPath('/cyberaudit')}
                    className={`dropdown-sub-link ${getSubLinkClass("Audit de sécurité")}`}
                    onMouseEnter={() => setHoveredSubLink("Audit de sécurité")}
                    onMouseLeave={() => setHoveredSubLink(null)}
                  >
                    {t('navbar.cyber.audit', 'Audit de sécurité')}
                  </a>
                  <a
                    href={getLangPath('/cyberincident')}
                    className={`dropdown-sub-link ${getSubLinkClass(
                      "Réponse à incident"
                    )}`}
                    onMouseEnter={() => setHoveredSubLink("Réponse à incident")}
                    onMouseLeave={() => setHoveredSubLink(null)}
                  >
                    {t('navbar.cyber.incident', 'Réponse aux incidents')}
                  </a>
                </div>

                <div className="column right-column">
                  <a
                    href={getLangPath('/sentinel')}
                    className={`dropdown-sub-link ${getSubLinkClass(
                      "Data Leaks Sentinel"
                    )}`}
                    onMouseEnter={() => setHoveredSubLink("Data Leaks Sentinel")}
                    onMouseLeave={() => setHoveredSubLink(null)}
                  >
                    {t('navbar.cyber.sentinel', 'Data Leaks Sentinel')}
                  </a>
                  <a
                    href={getLangPath('/devsecops')}
                    className={`dropdown-sub-link ${getSubLinkClass("DevSecOps")}`}
                    onMouseEnter={() => setHoveredSubLink("DevSecOps")}
                    onMouseLeave={() => setHoveredSubLink(null)}
                  >
                    {t('navbar.cyber.devSecOps', 'DevSecOps')}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
